///<reference path='../../../../../Builder/Utilities.ts' />
import { LitElement, html, css, unsafeCSS, TemplateResult, PropertyValues } from 'lit';
import { RDPropsetContainer } from '../Container/PropContainer.js';
import { keyed } from 'lit/directives/keyed.js';
import Sortable from 'sortablejs';
import { animate, stagger } from 'motion';
import { property } from 'lit/decorators.js';
///@ts-ignore
import styles from './Accordion.css?raw';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

interface AccordionItem {
  name: string;
  title: string;
  items: RDPropsetContainer[];
  expanded: boolean;
}

export class propAccordion extends RDPropsetContainer {
  static styles: any = [
    super.styles,
    css`
        ${unsafeCSS(styles)}
    `
  ];

  firstUpdated(): void {
    super.firstUpdated();
    this._setupSortable();
    this.updateSideBar();
  }

  updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties);

    if (changedProperties.has('canMove')) {
      queueMicrotask(() => {
        this._setupSortable();
      });
    }
    if (changedProperties.has('isDevices') || changedProperties.has('isThemes') || changedProperties.has('size')) {
      this.forceUpdate();
    }
    this._updateAllIndices();
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('rd-oncreated', () => {
      for (const items of this.items.values()) {
        items?.items?.forEach((item) => {
          if (item) {
            if ((item as any).element?.rd?.onCreated) {
              (item as any).element.rd.onCreated.bind(item)();
            }
            item.element.dispatchEvent(new CustomEvent('rd-oncreated'));
          }
        });
      }
    });
    this.addEventListener('rd-visibilityChanged', () => {
      for (const items of this.items.values()) {
        items?.items?.forEach((item) => {
          if (item) {
            if ((item as any).element?.rd?.visibilityChanged) {
              (item as any).element.rd.visibilityChanged.bind(item)();
            }
            item.element.dispatchEvent(new CustomEvent('rd-visibilityChanged'));
          }
        });
      }
    });

    this.addEventListener('rd-initialized', () => {
      for (const items of this.items.values()) {
        items?.items?.forEach((item) => {
          if (item) {
            if ((item as any).element?.rd?.initialized) {
              (item as any).element.rd.initialized.bind(item)();
            }
            item.element.dispatchEvent(new CustomEvent('rd-initialized'));
          }
        });
      }
    });

    for (const items of this.items.values()) {
      items?.items?.forEach((item) => {
        if (item) {
          item.element.addEventListener('update-sidebar', () => {
            this.requestUpdate();
            this.updateSideBar();
          });
        }
      });
    }
    this.updateSideBar();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._sortable) {
      this._sortable.destroy();
      this._sortable = null;
    }
    for (const items of this.items.values()) {
      items?.items?.forEach((item) => {
        if (item) {
          item.element.removeEventListener('update-sidebar', () => {
            this.requestUpdate();
            this.updateSideBar();
          });
        }
      });
    }
    this.updateSideBar();
  }

  private _setupSortable(): void {
    if (!this.canMove) return;

    const list = this.shadowRoot?.querySelector('.rd-accordion-list');
    if (!list) return;

    if (this._sortable) {
      this._sortable.destroy();
    }

    this._sortable = Sortable.create(list as HTMLElement, {
      animation: 150,
      handle: '.rd-accordion-drag-handle',
      draggable: '.rd-accordion-item',
      onEnd: (evt: any) => {
        const oldIndex = evt.oldIndex;
        const newIndex = evt.newIndex;

        if (oldIndex === newIndex || oldIndex === undefined || newIndex === undefined) return;

        // Recreate items map with new order
        const itemsArray = Array.from(this.items.values());
        const [movedItem] = itemsArray.splice(oldIndex, 1);
        itemsArray.splice(newIndex, 0, movedItem);

        // Rebuild map
        const newMap = new Map<string, AccordionItem>();
        itemsArray.forEach((item) => {
          newMap.set(item.name, item);
        });

        this['_items'] = newMap;
        this._updateAllIndices();
        this._forceUpdate();
        this.requestUpdate();

        if (this.onItemMoved) {
          this.onItemMoved.bind(this)(oldIndex, newIndex);
        }

        // Re-setup sortable after DOM updates
        queueMicrotask(() => {
          this._setupSortable();
        });
      }
    });
  }

  @property({ type: Function }) onAddClicked: Function = () => {
  };
  @property({ type: Function }) onDeleteClicked: Function = (item: AccordionItem) => {
  };
  @property({ type: Function }) onItemMoved: Function = (oldIndex: number, newIndex: number) => {
  };
  @property({ type: Boolean }) canDelete: boolean = true;
  @property({ type: Boolean }) canMove: boolean = true;
  @property({ type: Boolean }) canAdd: boolean = true;
  @property({ type: Boolean }) multiExpand: boolean = false;
  @property({ type: String }) addButtonLabel: string = 'Add Item';

  private _listKey: number = 0;
  private _sortable: any = null;

  constructor() {
    super();
    this['_items'] = new Map<string, AccordionItem>();
    this.isDevices = false;
    this.isThemes = false;
    this.size = 'full';
    this.clearIconVisible = false;
    this['_respectChildrenModified'] = true;
  }

  get _id(): string {
    let v = this['_uniqueId'];
    if (!v) {
      v = Utilities.generateID(true);
      this['_uniqueId'] = v;
    }
    return v;
  }

  // protected shouldUpdate(_changedProperties: PropertyValues): boolean {
  //   // if (_changedProperties.has('isDevices') || _changedProperties.has('isThemes') || _changedProperties.has('size')) {
  //   //   return false;
  //   // }
  //   super.shouldUpdate(_changedProperties);
  //   return true;
  // }

  private _forceUpdate(): void {
    this._listKey = Math.random();
  }

  get items(): Map<string, AccordionItem> {
    return (this['_items'] as Map<string, AccordionItem>) || new Map();
  }

  get groups(): RDPropsetContainer[][] {
    const result: RDPropsetContainer[][] = [];
    for (const items of this.items.values()) {
      result.push(items.items);
    }
    return result;
  }

  get respectChildrenModified(): boolean {
    return this['_respectChildrenModified'];
  }

  set respectChildrenModified(value: boolean) {
    this['_respectChildrenModified'] = value;
    this.requestUpdate();
    this.updateSideBar();
  }

  get  modified(): boolean {
    if (!this.respectChildrenModified) {
      return this['_modified'] || false;
    }
    let modified = false;
    for (const items of this.items.values()) {
      items?.items?.forEach((item) => {
        if (item) {
          if (item.modified) {
            modified = true;
          }
        }
      });
    }
    return modified;
  }

  set modified(value: boolean) {
    let oldValue = this.modified;
    this['_modified'] = value;
    super.requestUpdate('modified', oldValue);
    this.updateSideBar();
  }

  get totalItemsLength(): number {
    return this.items.size;
  }

  //public method
  addItem(item: AccordionItem): void {
    if (!item || !item.name) return;
    if (!this['_items']) {
      this['_items'] = new Map<string, AccordionItem>();
    }

    // Ensure items array exists
    if (!item.items) {
      item.items = [];
    }

    // If multiExpand is off and item is expanded, collapse all others
    if (!this.multiExpand && item.expanded) {
      this.items.forEach((existingItem) => {
        if (existingItem.expanded) {
          existingItem.expanded = false;
          this._notifyChildrenCollapsed(existingItem, true);
        }
      });
    }

    // Inject group references into nested propsets
    this._injectGroupReferences(item);
    this['_items'].set(item.name, item);
    this._forceUpdate();
    this.requestUpdate();

    // Re-setup sortable after adding item
    queueMicrotask(() => {
      this._setupSortable();
    });
    if (this['created'] === true) {
      if (item?.items?.length > 0) {
        for (let i = 0; i < item.items.length; i++) {
          const ps = item.items[i];
          if (ps?.element && (ps.element as any)['created'] !== true) {
            const el: any = ps.element;
            el.created = true;
            if (typeof el?.rd?.onCreated === 'function') {
              el.rd.onCreated.call(ps);
            }
            el.dispatchEvent(new CustomEvent('rd-oncreated'));
          }
        }
      }
    }
  }

  //public method
  removeItem(indexOrKey: number | string): void {
    const itemsArray = Array.from(this.items.values());
    let itemToRemove: AccordionItem | undefined;

    if (typeof indexOrKey === 'number') {
      if (indexOrKey < 0 || indexOrKey >= itemsArray.length) return;
      itemToRemove = itemsArray[indexOrKey];
    } else {
      itemToRemove = this.items.get(indexOrKey);
    }

    if (!itemToRemove) return;

    // Clear group references from nested propsets
    this._clearGroupReferences(itemToRemove);

    // Remove from map
    this.items.delete(itemToRemove.name);

    // Update indices for remaining items
    this._updateAllIndices();

    this._forceUpdate();
    this.requestUpdate();

    if (this.onDeleteClicked) {
      this.onDeleteClicked.bind(this)(itemToRemove);
    }

    // Re-setup sortable after DOM updates
    queueMicrotask(() => {
      this._setupSortable();
    });
  }

  removeAllItems(): void {
    this.items.forEach((item) => {
      this._clearGroupReferences(item);
    });
    this['_items'].clear();
    this.forceUpdate();
  }

  //public method
  expand(indexOrKey: number | string): void {
    const item = this._getItem(indexOrKey);
    if (!item) return;

    if (!this.multiExpand) {
      // Collapse all other items
      this.items.forEach((i) => {
        if (i !== item) {
          i.expanded = false;
          this._notifyChildrenCollapsed(i, true);
        }
      });
    }

    item.expanded = true;
    this._notifyChildrenCollapsed(item, false);
    this.requestUpdate();
  }

  //public method
  collapse(indexOrKey: number | string): void {
    const item = this._getItem(indexOrKey);
    if (!item) return;

    item.expanded = false;
    this._notifyChildrenCollapsed(item, true);
    this.requestUpdate();
  }

  //public method
  expandAll(): void {
    this.items.forEach((item) => {
      item.expanded = true;
      this._notifyChildrenCollapsed(item, false);
    });
    this.requestUpdate();
  }

  //public method
  collapseAll(): void {
    this.items.forEach((item) => {
      item.expanded = false;
      this._notifyChildrenCollapsed(item, true);
    });
    this.requestUpdate();
  }

  private _getItem(indexOrKey: number | string): AccordionItem | undefined {
    const itemsArray = Array.from(this.items.values());
    if (typeof indexOrKey === 'number') {
      return itemsArray[indexOrKey];
    }
    return this.items.get(indexOrKey);
  }

  private _injectGroupReferences(item: AccordionItem): void {
    if (!item.items || item.items.length === 0) return;

    const index = Array.from(this.items.values()).indexOf(item);

    item.items.forEach((propset, idx) => {
      (propset as any).element.accordionGroup = item;
      (propset as any).element.accordionGroupName = item.name;
      (propset as any).element.accordionGroupIndex = idx;

      // Assign slot and append to light DOM if not already there
      const slotName = `${item.name}-propset-${idx}`;
      propset?.element?.setAttribute('slot', slotName);

      // Only append if not already a child
      if (propset?.element?.parentElement !== this) {
        this.appendChild(propset?.element);
      }
      propset?.element.forceUpdate();
    });
  }

  private _clearGroupReferences(item: AccordionItem): void {
    if (!item.items || item.items.length === 0) return;

    item.items.forEach((propset) => {
      delete (propset as any).element.accordionGroup;
      delete (propset as any).element.accordionGroupName;
      delete (propset as any).element.accordionGroupIndex;

      // Remove slot attribute and from light DOM
      propset?.element?.removeAttribute('slot');
      if (propset?.element?.parentElement === this) {
        this.removeChild(propset?.element);
        propset?.element.forceUpdate();
      }
    });
  }

  private _updateAllIndices(): void {
    this.items.forEach((item) => {
      this._injectGroupReferences(item);
    });

    queueMicrotask(() => {
      this._setupSortable();
    });
    // Force update all nested propsets
    // this.items.forEach((item) => {
    //   if (!item.items || item.items.length === 0) return;
    //   item.items.forEach((propset) => {
    //     if (typeof (propset as any).element.forceUpdate === 'function') {
    //       (propset as any).element.forceUpdate();
    //     }
    //   });
    // });
  }

  private _notifyChildrenCollapsed(item: AccordionItem, collapsed: boolean): void {
    if (!item.items || item.items.length === 0) return;

    item.items.forEach((propset) => {
      if (typeof (propset as any).visibilityChanged === 'function') {
        (propset as any).visibilityChanged(collapsed);
      }
    });
  }

  private _toggleItem(item: AccordionItem): void {
    const itemElement = this.shadowRoot?.querySelector(`[data-name="${item.name}"]`);
    const contentElement = itemElement?.querySelector('.rd-accordion-content') as HTMLElement;

    if (!contentElement) {
      // Fallback if element not found
      if (item.expanded) {
        this.collapse(item.name);
      } else {
        this.expand(item.name);
      }
      return;
    }

    if (item.expanded) {
      // Animate collapse
      const currentHeight = contentElement.scrollHeight;
      animate(contentElement, { 'max-height': [currentHeight + 'px', '0px'], opacity: [1, 0] } as any, {
        duration: 0.2,
        easing: [0.4, 0, 0.2, 1]
      } as any).finished.then(() => {
        this.collapse(item.name);
      });
    } else {
      // Set initial state before expanding
      item.expanded = true;
      this._notifyChildrenCollapsed(item, false);

      if (!this.multiExpand) {
        // Collapse all other items with animation
        this.items.forEach((i) => {
          if (i !== item && i.expanded) {
            const otherItemElement = this.shadowRoot?.querySelector(`[data-name="${i.name}"]`);
            const otherContent = otherItemElement?.querySelector('.rd-accordion-content') as HTMLElement;
            if (otherContent) {
              const otherHeight = otherContent.scrollHeight;
              animate(otherContent, {
                'max-height': [otherHeight + 'px', '0px'],
                opacity: [1, 0]
              } as any, { duration: 0.2, easing: [0.4, 0, 0.2, 1] } as any);
            }
            i.expanded = false;
            this._notifyChildrenCollapsed(i, true);
          }
        });
      }

      this.requestUpdate();

      // Animate expand after DOM update
      // Use longer delay to ensure child propsets with devices/themes have rendered
      queueMicrotask(() => {
        const updatedContent = this.shadowRoot?.querySelector(`[data-name="${item.name}"] .rd-accordion-content`) as HTMLElement;
        if (updatedContent) {
          // Wait for child propsets to fully render before calculating height
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const targetHeight = updatedContent.scrollHeight;
              animate(updatedContent, {
                'max-height': ['0px', targetHeight + 'px'],
                opacity: [0, 1]
              } as any, { duration: 0.25, easing: [0.4, 0, 0.2, 1] } as any);
            });
          });
        }
      });
    }
  }

  _renderContainer(): TemplateResult<1> {
    return html`
      <div class="rd-propset-accordion"></div> `;
  }

  renderBody(): TemplateResult<1> {
    const itemsArray = Array.from(this.items.values());

    if (itemsArray.length === 0) {
      return html`
        <div class="rd-accordion-empty">${this.canAdd ? html`
          <button class="rd-accordion-add-btn" @click="${() => this._handleAddClick()}">
            ${unsafeHTML(this.addButtonLabel)}
          </button> ` : ''}
        </div> `;
    }

    return html`
      ${keyed(
        this._listKey,
        html`
          <div class="rd-accordion-list">
            ${itemsArray.map((item, index) => this._renderAccordionItem(item, index))}
            ${this.canAdd
              ? html`
                <div class="rd-accordion-add-container">
                  <button class="rd-accordion-add-btn" @click="${() => this._handleAddClick()}">
                    ${unsafeHTML(this.addButtonLabel)}
                  </button>
                </div>
              `
              : ''}
          </div>
        `
      )}
    `;
  }

  private _renderAccordionItem(item: AccordionItem, index: number): TemplateResult<1> {
    let hasModified = false;
    for (const items of item.items) {
      if (items.modified) {
        hasModified = true;
      }
    }
    return html`
      <div class="rd-accordion-item ${item.expanded ? 'expanded' : 'collapsed'}" data-name="${item.name}"
           data-index="${index}">
        <div class="rd-accordion-header ${hasModified ? 'modified' : ''}" @click="${() => this._toggleItem(item)}">
          <div class="rd-accordion-header-content">
            <span class="rd-accordion-toggle-icon"> ${item.expanded ? '▼' : '▶'} </span>
            <span class="rd-accordion-title">${item.title}</span>
          </div>
          <div class="rd-accordion-controls" @click="${(e: Event) => e.stopPropagation()}">
            ${this.canMove ? html`<span class="rd-accordion-drag-handle" title="Drag to reorder">⋮⋮</span>` : ''}
            ${this.canDelete ? html`
              <button class="rd-accordion-delete-btn" @click="${() => this._handleDeleteClick(item)}" title="Delete">×
              </button> ` : ''}
          </div>
        </div>
        <div class="rd-accordion-content ${item.expanded ? 'expanded' : 'collapsed'}">
          ${item.items && item.items.length > 0 ? html`
            <div class="rd-accordion-propsets">${item.items.map((propset, idx) => html`
              <slot name="${item.name}-propset-${idx}"></slot>`)}
            </div> ` : html`
            <div class="rd-accordion-empty-content">No items</div> `}
        </div>
      </div>
    `;
  }

  private _handleAddClick(): void {
    if (this.onAddClicked) {
      this.onAddClicked.bind(this)();
    }
  }

  private _handleDeleteClick(item: AccordionItem): void {
    this.removeItem(item.name);
  }
}

if (!customElements.get('rd-prop-accordion')) {
  customElements.define('rd-prop-accordion', propAccordion);
}
